import { useEffect, useState, useTransition } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { useInitViewsAndFilters, useRidesActions } from "@ryde/redux";
import {
  activeViewIdAtomFamily,
  apiDataFetchingAtom,
  checkIsAllInit,
  controlsSF,
  finalSchemaInitStateSF,
  Log,
  ridesIdSearchRefetchTokenAtom,
  RydeUiUnit,
  topCalendarDatesAtomFamily,
} from "@ryde/shared";

import { buildRidesFetchKey } from "./use-rides-page.utils";

export const useRidesPage = (entity: RydeUiUnit) => {
  const { fetchRides } = useRidesActions();
  const calendarDates = useRecoilValue(topCalendarDatesAtomFamily(entity));
  const controls = useRecoilValue(controlsSF(entity));
  const isSchemaLoaded = controls?.length && controls.length > 0;
  const activeViewId = useRecoilValue(activeViewIdAtomFamily(entity));
  const finalSchemaInitState = useRecoilValue(finalSchemaInitStateSF(entity));
  const idSearchRefetchToken = useRecoilValue(ridesIdSearchRefetchTokenAtom);
  const [, startTransition] = useTransition();
  const [isPageReady, setIsPageReady] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const setIsLoading = useSetRecoilState(apiDataFetchingAtom);

  useInitViewsAndFilters({
    entity,
    searchQueryToOptimize: "rides",
    // The schema is fetched by entity name, so it can be ready before the IDs the views request needs.
    isPageDependenciesReady: Boolean(isSchemaLoaded),
    onInitStart: () => setIsLoading(true),
    onInitialized: () => setIsPageReady(true),
    onInitSkipped: () => {
      setIsPageReady(false);
      // The rides fetch below is what clears this loader, and it is gated on `isPageReady`. An init that is
      // skipped after a previous one turned the loader on must therefore release it here.
      setIsLoading(false);
    },
  });

  useEffect(() => {
    if (
      // filterObjChangesForRefetch &&   => ** DO NOT DELETE - Development under review for active filters on click apply only **
      calendarDates &&
      isInitializing &&
      isSchemaLoaded
    ) {
      setIsInitializing(false);
    }
  }, [calendarDates, isInitializing, isSchemaLoaded]);

  // Wait for view init, not just `isPageReady`: `useFinalSchemaService` auto-selects the default view
  // in a later effect chain, so fetching on `isPageReady` alone searches with `activeViewId` undefined
  // and again when it lands. Same predicate the grid waits on (`use-table-wrapper.ts`); its last flag
  // is set even for a company with no views, so it can't stall. Guarding on `activeViewId` itself
  // would instead stop fetching entirely for a company that has no default view.
  const isSchemaInitComplete = checkIsAllInit(finalSchemaInitState);
  const isReadyToFetch = !isInitializing && isPageReady && isSchemaInitComplete;
  const fetchKey = buildRidesFetchKey({
    activeViewId,
    calendarDates,
    idSearchRefetchToken,
  });

  useEffect(() => {
    const abortController = new AbortController();
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    if (isReadyToFetch) {
      Log.debug("Loading page for ", fetchKey);

      startTransition(() => {
        timeoutId = setTimeout(() => {
          fetchRides({ abortController })
            .finally(() => {
              setIsLoading(false);
            })
            .catch(error => Log.warn("Error fetching rides", error));
        });
      });
    }

    return () => {
      // Clearing matters as much as aborting: `fetchRides` hits `RidesApi.count` first and that call
      // takes no signal, so a superseded timeout would still leave a count request in flight.
      clearTimeout(timeoutId);
      abortController.abort();
      Log.debug("Fetching rides aborted on page unmount");
    };
  }, [isReadyToFetch, fetchKey, fetchRides, setIsLoading]);

  useEffect(() => {
    return () => {
      setIsPageReady(false);
    };
  }, []);
};
